<template>
  <div
    v-if="selected_report"
    id="navbar-sb-wrapper"
    class="navbar-sb-wrapper w-100"
    @mouseleave="handleWrapperMouseLeave"
  >
    <b-navbar
      toggleable="lg"
      type="light"
      fixed="top"
      class="navbar-sb w-100"
      :class="{ 'scrolled-navbar': isScrolled }"
      :style="{
        backgroundColor: ai_main_page_url && !selected_lv1 ? navbarBackgroundColor : 'white'
      }"
    >
      <b-row class="flex-grow-1 d-flex justify-content-between padded-container" no-gutters>
        <b-col cols="4" xl="2" sxl="2" class="align-items-center">
          <b-navbar-brand class="cursor" @click="go_sb_main">
            <b-img
              id="sb-logo-img"
              class="navbar-sb-logo"
              :src="$store.state.static_host + selected_report.logo_path"
            />
          </b-navbar-brand>
        </b-col>

        <b-col
          v-if="!showSearchBar"
          class="d-none d-xl-flex justify-content-around justify-content-xl-center align-items-center"
          cols="8"
          @mouseover="showMenu"
        >
          <b-navbar-nav
            no-gutters
            class="d-none d-xl-flex navbar-sb-menu position-relative"
            :class="{ 'navbar-sb-menu-expanded': isHoverMainMenu }"
          >
            <b-nav-item
              v-for="(lv1_info, lv1_idx) in sb_toc"
              :key="'nav_lv1' + lv1_idx"
              href="#"
              class="flex-grow-1 nav-item-custom align-items-center"
              :link-classes="['flex-grow-1 fw-600 py-0 px-0 nav-link-equal']"
              @mouseover="handleHoverMenuItem(getLocalizedName(lv1_info, lang))"
              @mouseleave="resetHoverMenuItem"
              @click="update_lv1_info(lv1_info)"
            >
              <span
                class="nav-link-custom"
                :class="isLv1Active(lv1_info.key) ? 'nav-link-custom-active' : ''"
                :style="{ color: ai_main_page_url && !selected_lv1 ? navbarTextColor : 'black' }"
              >
                {{ getLocalizedName(lv1_info, lang) }}
              </span>
            </b-nav-item>
          </b-navbar-nav>
        </b-col>
        <b-col
          v-else
          class="d-none d-md-flex justify-content-around justify-content-center"
          cols="4"
          xl="8"
        >
          <b-navbar-nav no-gutters class="d-flex position-relative">
            <b-input-group class="my-3 search-input-group">
              <b-form-input
                ref="report_search_form"
                v-model="localSearchKeyword"
                :placeholder="searchPlaceholder"
                class="search-input"
                @keyup.enter="performSearch"
                @keyup.esc="closeSearchBar"
              />
              <b-input-group-append>
                <b-button variant="none" class="search-button" @click="performSearch">
                  <b-icon icon="search" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-navbar-nav>
        </b-col>

        <b-col
          cols="6"
          sm="5"
          md="2"
          offset-md="2"
          offset-xl="0"
          class="d-flex justify-content-end align-items-center"
        >
          <b-navbar-nav
            class="d-flex flex-row justify-content-around justify-content-xl-end align-items-center w-100 nav-icons-wrap"
          >
            <b-nav-item class="d-xl-none">
              <b-btn
                v-b-tooltip.hover.bottom="{ disabled: isTouchDevice }"
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :style="{ color: ai_main_page_url && !selected_lv1 ? navbarIconColor : '' }"
                :title="lang != 'ENG' ? '메뉴' : 'Menu'"
                @click="onHamburgerClick"
              >
                <svg
                  width="28"
                  height="28"
                  class="hamburger-icon"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="4" y="12" width="48" height="3" fill="currentColor" />
                  <rect x="4" y="27" width="48" height="3" fill="currentColor" />
                  <rect x="4" y="42" width="48" height="3" fill="currentColor" />
                </svg>
              </b-btn>
            </b-nav-item>
            <b-nav-item>
              <b-btn
                v-b-tooltip.hover.bottom
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :style="{ color: ai_main_page_url && !selected_lv1 ? navbarIconColor : '' }"
                :title="lang != 'ENG' ? '검색하기' : 'Search'"
                @mouseover="handleHoverIcon('print')"
                @mouseleave="resetHoverIcon"
                @click="toggleSearchBar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="currentColor"
                  class="bi bi-search"
                  viewBox="0 0 20 20"
                  style="transform: translateY(3px)"
                >
                  <path
                    d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"
                  />
                </svg>
              </b-btn>
            </b-nav-item>

            <b-nav-item
              v-if="theme_option?.theme_info?.show_printing_btn"
              class="d-none d-xl-block"
            >
              <b-btn
                v-b-tooltip.hover.bottom
                :disabled="!pdfReady"
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :style="{ color: ai_main_page_url && !selected_lv1 ? navbarIconColor : '' }"
                :title="
                  !pdfReady
                    ? 'pdf 출력을 준비중입니다.'
                    : lang != 'ENG'
                    ? 'PDF 다운로드'
                    : 'PDF Download'
                "
                @click="clickExportPdf"
                @mouseover="handleHoverIcon('print')"
                @mouseleave="resetHoverIcon"
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.9771 12.0494C17.9592 11.9191 17.8544 11.7555 17.742 11.6456C17.4199 11.3287 16.712 11.1626 15.6334 11.1472C14.9025 11.1396 14.0233 11.2035 13.0981 11.3338C12.684 11.0961 12.2572 10.8354 11.9224 10.5211C11.0202 9.6802 10.2688 8.5122 9.79851 7.22663C9.82918 7.10651 9.85473 7.00172 9.88029 6.89437C9.88029 6.89437 10.3863 4.01142 10.2534 3.0351C10.2355 2.9022 10.2228 2.86386 10.187 2.75908L10.1435 2.64662C10.0055 2.32714 9.73461 1.98978 9.31035 2.00767L9.05988 2H9.05477C8.57939 2 8.19601 2.2428 8.09378 2.60317C7.78709 3.73795 8.10401 5.43756 8.67906 7.63556L8.53083 7.99337C8.11934 9.00036 7.60307 10.0125 7.14813 10.9044L7.08935 11.0194C6.60886 11.9574 6.17437 12.7548 5.77822 13.4296L5.37185 13.6443C5.34118 13.6596 4.64344 14.0302 4.47987 14.1273C3.09207 14.9579 2.17197 15.8985 2.01863 16.6448C1.97007 16.885 2.00585 17.1892 2.25376 17.3297L2.64735 17.5291C2.81859 17.6134 3.00006 17.6569 3.18407 17.6569C4.17317 17.6569 5.32073 16.425 6.90278 13.6647C8.72762 13.0718 10.8081 12.5759 12.6303 12.305C14.0181 13.0845 15.7254 13.6289 16.804 13.6289C16.9957 13.6289 17.1618 13.611 17.2947 13.5753C17.5017 13.519 17.6755 13.4015 17.7828 13.243C17.9924 12.9286 18.0333 12.4941 17.9771 12.0494ZM2.96939 16.908C3.14829 16.4148 3.86392 15.4384 4.91947 14.5746C4.98592 14.5209 5.14949 14.3676 5.29773 14.2244C4.19362 15.9854 3.45499 16.6857 2.96939 16.908ZM9.22089 2.51116C9.53781 2.51116 9.71928 3.31113 9.73461 4.06254C9.74995 4.81395 9.57359 5.34044 9.35635 5.73148C9.17489 5.15387 9.08799 4.244 9.08799 3.6485C9.08799 3.6485 9.07521 2.51116 9.22089 2.51116ZM7.35515 12.7702C7.57751 12.374 7.80753 11.9549 8.04267 11.5127C8.61772 10.4265 8.98065 9.57797 9.24901 8.88024C9.78573 9.85655 10.4553 10.6872 11.24 11.3517C11.3397 11.4335 11.4444 11.5178 11.5518 11.6022C9.95441 11.9165 8.57172 12.3025 7.35515 12.7702ZM17.4327 12.6807C17.3356 12.7395 17.057 12.7753 16.8781 12.7753C16.3005 12.7753 15.5823 12.512 14.5779 12.0801C14.9638 12.052 15.3191 12.0367 15.636 12.0367C16.2161 12.0367 16.3874 12.0341 16.9573 12.1798C17.5247 12.3255 17.5324 12.6194 17.4327 12.6807Z"
                    fill="currentColor"
                  />
                </svg>
              </b-btn>
            </b-nav-item>
            <b-nav-item v-if="theme_option?.theme_info?.show_translation_btn">
              <b-btn
                v-b-tooltip.hover.bottom
                :disabled="disableButtons"
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :style="{ color: ai_main_page_url && !selected_lv1 ? navbarIconColor : '' }"
                :title="lang != 'ENG' ? '영문보기' : 'Korean'"
                @click="toggleLang"
                @mouseover="handleHoverIcon('lang')"
                @mouseleave="resetHoverIcon"
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="28" cy="28" r="21.5" stroke="currentColor" stroke-width="3" />
                  <path
                    d="M36.0837 28C36.0837 34.2027 35.0333 39.7479 33.3921 43.6865C32.5702 45.6591 31.6297 47.1575 30.662 48.1399C29.6995 49.1169 28.801 49.5 28.0003 49.5C27.1997 49.5 26.3011 49.1169 25.3386 48.1399C24.3709 47.1575 23.4304 45.6591 22.6085 43.6865C20.9674 39.7479 19.917 34.2027 19.917 28C19.917 21.7973 20.9674 16.2521 22.6085 12.3135C23.4304 10.3409 24.3709 8.84246 25.3386 7.86013C26.3011 6.88308 27.1997 6.5 28.0003 6.5C28.801 6.5 29.6995 6.88308 30.662 7.86013C31.6297 8.84246 32.5702 10.3409 33.3921 12.3135C35.0333 16.2521 36.0837 21.7973 36.0837 28Z"
                    stroke="currentColor"
                    stroke-width="3"
                  />
                  <path d="M6.91602 28H49.0827" stroke="currentColor" stroke-width="3" />
                </svg>
              </b-btn>
            </b-nav-item>
            <b-nav-item
              v-if="$route.path === '/report_preview' || $route.path === '/custom_css_editor'"
            >
              <b-button
                v-b-tooltip.hover
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :style="{ color: ai_main_page_url && !selected_lv1 ? navbarIconColor : '' }"
                :title="lang != 'ENG' ? '미리보기 닫기' : 'Close preview'"
                @click="handleClosePreview"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  class="bi bi-x-lg"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"
                  />
                </svg>
              </b-button>
            </b-nav-item>
            <b-nav-item v-if="$route.query?.from === 'translate'">
              <b-button
                v-b-tooltip.hover
                variant="none"
                size="lg"
                class="icon-button text-decoration-none border-0 text-secondary shadow-none p-0"
                :style="{ color: ai_main_page_url && !selected_lv1 ? navbarIconColor : '' }"
                :title="lang != 'ENG' ? '번역 페이지로 돌아가기' : 'Back to translate'"
                @click="handleBackToTranslate"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="26"
                  height="26"
                  fill="currentColor"
                  class="bi bi-arrow-return-left"
                  viewBox="0 0 16 16"
                >
                  <path
                    fill-rule="evenodd"
                    d="M14.5 1.5a.5.5 0 0 1 .5.5v4.8a2.5 2.5 0 0 1-2.5 2.5H2.707l3.347 3.346a.5.5 0 0 1-.708.708l-4.2-4.2a.5.5 0 0 1 0-.708l4.2-4.2a.5.5 0 1 1 .708.708L2.707 8.3H12.5A1.5 1.5 0 0 0 14 6.8V2a.5.5 0 0 1 .5-.5"
                  />
                </svg>
              </b-button>
            </b-nav-item>
          </b-navbar-nav>
        </b-col>

        <b-modal
          id="story-board-modal"
          centered
          size="lg"
          title="PDF 다운로드"
          hide-footer
          no-close-on-backdrop
          no-close-on-esc
        >
          <SbPrintOption />
        </b-modal>
      </b-row>
    </b-navbar>

    <!-- 모바일 전용 검색바: md 미만에서만 노출. 기존 inline 검색은 md+에서 navbar 내부에 표시됨 -->
    <MobileSearchBar
      ref="mobileSearchBar"
      v-model="localSearchKeyword"
      :show="showSearchBar"
      :placeholder="searchPlaceholder"
      :lang="lang"
      @search="performSearch"
      @close="closeSearchBar"
    />

    <search v-if="selected_lv1" />

    <!-- 목차 드롭다운 메뉴 -->
    <div
      v-if="showDropdown"
      class="submenu border-bottom"
      :class="{ 'submenu-slide-down': isHoverMainMenu, 'border-top': !isScrolled }"
    >
      <b-row no-gutters class="justify-content-center w-100 padded-container">
        <b-col cols="12" xl="8" class="d-none d-sm-flex justify-content-between">
          <div
            v-for="(lv1_info, lv1_idx) in sb_toc"
            :key="'dropdown_lv1' + lv1_idx"
            class="flex-grow-1 flex-column navbar-submenu justify-content-start nav-item-custom2 py-4 px-2"
          >
            <div
              v-for="(lv2_info, lv2_idx) in lv1_info.children"
              :key="'dropdown_lv2' + lv2_idx"
              @mouseover="handleHoverMenuItem2(lv1_info.name)"
              @mouseleave="resetHoverMenuItem2"
              @click="update_lv2_info(lv1_info, lv2_info)"
            >
              <div
                v-if="
                  lv2_info.appendix_type === null || report_types.includes(lv2_info.appendix_type)
                "
                class="link-container"
              >
                <b-link
                  class="submenu-link"
                  :class="{ 'disabled-link': isLv2Active(lv2_info.key) }"
                >
                  <span
                    :class="[
                      {
                        hovered: hoveredLink === lv2_info.name,
                        'non-hovered': hoveredLink && hoveredLink !== lv2_info.name
                      },
                      isLv2Active(lv2_info.key) ? 'disabled-link-active' : ''
                    ]"
                  >
                    {{ getLocalizedName(lv2_info, lang) }}
                  </span>
                </b-link>
              </div>
            </div>
          </div>
        </b-col>

        <b-col cols="12" class="d-flex d-sm-none">
          <div class="accordion w-100 mobile-submenu" role="tablist">
            <div v-for="(lv1_info, lv1_idx) in sb_toc" :key="'dropdown_lv1_' + lv1_idx" class="">
              <div class="border-bottom-gray-900" role="tab">
                <b-button
                  v-b-toggle="'accordion-' + lv1_idx"
                  block
                  variant="none"
                  class="mobile-submenu-lv1"
                >
                  {{ getLocalizedName(lv1_info, lang) }}
                </b-button>
              </div>
              <b-collapse
                :id="'accordion-' + lv1_idx"
                role="tabpanel"
                :visible="lv1_idx === 0"
                accordion="my-accordion"
              >
                <div class="d-flex flex-column">
                  <b-button
                    v-for="(lv2_info, lv2_idx) in lv1_info.children"
                    :key="'dropdown_lv2_' + lv1_idx + '_' + lv2_idx"
                    :class="{ 'disabled-link': isLv2Active(lv2_info.key) }"
                    variant="light"
                    class="mobile-submenu-lv2"
                    @click="update_lv2_info(lv1_info, lv2_info)"
                  >
                    {{ getLocalizedName(lv2_info, lang) }}
                  </b-button>
                </div>
              </b-collapse>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import SbPrintOption from '@/components/report_preview/story_board/viewer_component/SbPrintOption.vue';
import MobileSearchBar from '@/components/report_preview/story_board/viewer_component/MobileSearchBar.vue';
import search from '@/components/search_bar/search.vue';
import { updateParamsFromQueryString } from '../../../util';
import { getLocalizedName } from '@/utils/i18n';
export default {
  name: 'Nav',
  components: {
    SbPrintOption,
    MobileSearchBar,
    search
  },

  data() {
    return {
      showDropdown: true,
      hoveredMenuItem: null,
      hoveredIcon: null,
      hoveredLink: null,
      isHoverMainMenu: false,
      isScrolled: false,
      scrollY: 0,
      showSearchBar: false,
      localSearchKeyword: '',
      searchResults: null,
      loading: true,
      disableButtons: true,
      // 터치 디바이스 감지 — handleWrapperMouseLeave의 synthetic mouseleave 가드용
      isTouchDevice: false,
      // 햄버거 click 직후 짧은 시간 mouseleave를 무시하기 위한 timestamp
      lastHamburgerClickAt: 0
    };
  },
  computed: {
    ...mapGetters(['is_viewer']),
    ...mapState('assurance', ['assurance_status']),
    ...mapState('report_list', ['lang', 'view_mode']),
    ...mapGetters('report_list', ['selected_report', 'current_report_code']),
    ...mapGetters('story_board', [
      'selected_lv1',
      'selected_lv2',
      'sb_toc',
      'selected_viewer_disclosures',
      'ai_main_page_url'
    ]),
    ...mapState('story_board', [
      'disclosures_loaded',
      'toc_loaded',
      'is_fetching_lv2_disclosure',
      'main_setting',
      'print_mode',
      'viewer_disclosure_map',
      'viewer_disclosure_map_loaded'
    ]),
    ...mapState('report_contents', ['theme_option']),
    ...mapState('assurance', ['assurance_list_loaded']),
    ...mapState('preview_route', ['globalString']),
    ...mapGetters('assurance', ['latest_assurance']),
    pdfReady() {
      return (
        this.toc_loaded &&
        !this.is_fetching_lv2_disclosure &&
        this.main_setting !== null &&
        this.theme_option !== null &&
        this.assurance_list_loaded &&
        this.viewer_disclosure_map_loaded
      );
    },
    report_types() {
      const user_selected_report_type = Object.keys(this.selected_report.report_type).map(
        item => item.split('v')[0]
      );
      if (this.latest_assurance) {
        user_selected_report_type.push('ASSURANCE');
      }

      if (this.selected_report?.data_center) {
        user_selected_report_type.push('DATACENTER');
      }
      return user_selected_report_type;
    },
    mainColor() {
      const lv1Color = this.selected_lv1?.background_color;
      if (lv1Color && lv1Color !== '#014555') {
        return lv1Color;
      }
      return this.selected_report?.main_color || '#008C95';
    },
    navbarBackgroundColor() {
      // 호버 시에는 항상 불투명한 흰색
      if (this.isHoverMainMenu && this.ai_main_page_url) {
        return 'rgba(255, 255, 255, 1)';
      }
      // 0~200px 사이에서 투명도를 0에서 1로 변화
      const maxScroll = 200;
      const opacity = Math.min(this.scrollY / maxScroll, 1);
      return `rgba(255, 255, 255, ${opacity})`;
    },
    navbarTextColor() {
      // 호버 시에는 항상 검은색
      if (this.isHoverMainMenu) {
        return 'rgb(17, 17, 17)';
      }
      // 스크롤 위치에 따라 흰색(255)에서 검은색(17)으로 변화
      const maxScroll = 200;
      const progress = Math.min(this.scrollY / maxScroll, 1);
      const r = Math.round(255 - (255 - 17) * progress);
      const g = Math.round(255 - (255 - 17) * progress);
      const b = Math.round(255 - (255 - 17) * progress);
      return `rgb(${r}, ${g}, ${b})`;
    },
    navbarIconColor() {
      // 호버 시에는 항상 검은색
      if (this.isHoverMainMenu) {
        return 'rgba(17, 17, 17, 0.5)';
      }
      // 아이콘의 경우 좀 더 명확한 전환 (흰색에서 회색으로)
      const maxScroll = 200;
      const progress = Math.min(this.scrollY / maxScroll, 1);
      const opacity = 0.5 + progress * 0.5; // 0.5에서 1로
      const colorValue = Math.round(255 - (255 - 17) * progress);
      return `rgba(${colorValue}, ${colorValue}, ${colorValue}, ${opacity})`;
    },
    searchPlaceholder() {
      return this.lang === 'ENG' ? 'Enter search keyword...' : '키워드를 입력하세요...';
    }
  },

  watch: {
    lang() {
      this.$nextTick(() => {
        this.updateNavPointColor();
      });
    },
    selected_lv1() {
      this.$nextTick(() => {
        this.updateNavPointColor();
      });
    }
  },
  mounted() {
    setTimeout(() => {
      this.disableButtons = false;
    }, 5000);
    this.updateNavPointColor();
    window.addEventListener('scroll', this.handleScroll);
    this.isTouchDevice =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    ...mapMutations('report_list', ['change_lang']),
    ...mapMutations('story_board', ['update_selected_lv1', 'update_selected_lv2']),
    ...mapMutations('search', ['setSearchKeyword']),
    ...mapActions('story_board', ['fetch_viewer_sb_disclosures']),
    getLocalizedName,
    clickExportPdf() {
      this.$store.commit('story_board/update_print_mode', true);
      this.$bvModal.show('story-board-modal');
    },
    closeSearch() {
      this.toggleSearchBar();
      this.localSearchKeyword = '';
      this.searchResults = null;
      this.loading = false;
    },
    performSearch() {
      const searchword = this.localSearchKeyword;

      // 검색어가 비어있을 때 처리
      if (!searchword || !searchword.trim()) {
        // 경고 표시 또는 아무것도 하지 않음
        if (this.$refs.report_search_form) {
          this.$refs.report_search_form.focus();
        }
        return;
      }

      // 검색 실행
      this.setSearchKeyword(searchword.trim());

      // 검색 후 검색 바 자동으로 닫기
      this.showSearchBar = false;
    },
    closeSearchBar() {
      this.showSearchBar = false;
      this.localSearchKeyword = '';
    },
    isLv1Active(lv1Key) {
      return this.selected_lv1?.key === lv1Key;
    },

    isLv2Active(lv2Key) {
      return this.selected_lv2?.key === lv2Key;
    },
    go_sb_main() {
      window.scrollTo(0, 0);
      // 검색 결과창에서 벗어나기
      this.setSearchKeyword('');

      this.update_selected_lv1({ lv1_info: null, view_mode: this.is_viewer ? 'viewer' : 'editor' });
      this.update_selected_lv2({ lv2_info: null, view_mode: this.is_viewer ? 'viewer' : 'editor' });
      updateParamsFromQueryString('lv1', null, true);
      updateParamsFromQueryString('lv2', null, true);
      updateParamsFromQueryString('q_str', null, true);
    },
    showMenu() {
      this.isHoverMainMenu = true;
      this.showDropdown = true;
    },
    hideMenu() {
      this.showDropdown = true;
      this.isHoverMainMenu = false;
    },
    // 터치 디바이스에서는 click 직후 synthetic mouseleave가 발생할 수 있어
    // 햄버거를 막 누른 직후 짧은 시간(400ms) 동안은 hideMenu를 무시한다.
    handleWrapperMouseLeave() {
      if (this.isTouchDevice) return;
      const sinceHamburger = Date.now() - this.lastHamburgerClickAt;
      if (sinceHamburger < 400) return;
      this.hideMenu();
    },
    onHamburgerClick() {
      this.lastHamburgerClickAt = Date.now();
      // 메뉴가 실제로 보이는 상태는 showDropdown && isHoverMainMenu 둘 다 true일 때.
      // 항목 선택 직후엔 showDropdown이 false라 isHoverMainMenu가 true여도 메뉴가 안 보임 →
      // 단순 토글이 아니라 실제 가시성 기준으로 open/close 판정.
      const isMenuVisible = this.showDropdown && this.isHoverMainMenu;
      if (isMenuVisible) {
        // 닫기 (슬라이드 업 애니메이션 유지)
        this.isHoverMainMenu = false;
      } else {
        // 열기 (DOM/클래스 모두 강제로 보이는 상태로)
        this.showDropdown = true;
        this.isHoverMainMenu = true;
        // 검색바와 동시에 뜨면 UI가 겹치므로 검색바 닫기
        if (this.showSearchBar) {
          this.showSearchBar = false;
          this.localSearchKeyword = '';
        }
      }
    },
    toggleLang() {
      if (this.lang == 'KOR') {
        this.change_lang('ENG');
        document.querySelector('title').textContent = this.selected_report.report_name_e;
        updateParamsFromQueryString('l', 'ENG', false);
      } else {
        this.change_lang('KOR');
        document.querySelector('title').textContent = this.selected_report.report_name;
        updateParamsFromQueryString('l', null, false);
      }
    },
    update_lv1_info(lv1_info) {
      window.scrollTo(0, 0);
      // 검색 결과창에서 벗어나기
      this.setSearchKeyword('');

      const view_mode = this.is_viewer ? 'viewer' : 'editor';
      const first_lv2 =
        lv1_info.children && lv1_info.children.length > 0 ? lv1_info.children[0] : null;

      this.update_selected_lv1({
        lv1_info: lv1_info,
        view_mode: view_mode
      });

      if (this.is_viewer) {
        this.update_selected_lv2({
          lv2_info: first_lv2,
          view_mode: view_mode
        });
        updateParamsFromQueryString('lv2', first_lv2 ? first_lv2.name : null, true);
      } else {
        this.update_selected_lv2({
          lv2_info: first_lv2,
          view_mode: view_mode
        });
      }

      updateParamsFromQueryString('lv1', lv1_info.name, true);
      updateParamsFromQueryString('q_str', null, true);

      if (this.is_viewer) {
        this.fetch_viewer_sb_disclosures({
          report_code: this.current_report_code,
          selected_lv_info: {
            lv1_info: lv1_info,
            lv2_info: first_lv2
          }
        });
      }
    },
    update_lv2_info(lv1_info, lv2_info) {
      window.scrollTo(0, 0);
      // 검색 결과창에서 벗어나기
      this.setSearchKeyword('');

      this.update_selected_lv1({
        lv1_info: lv1_info,
        view_mode: this.is_viewer ? 'viewer' : 'editor'
      });
      this.update_selected_lv2({
        lv2_info: lv2_info,
        view_mode: this.is_viewer ? 'viewer' : 'editor'
      });

      updateParamsFromQueryString('lv1', lv1_info.name, true);
      updateParamsFromQueryString('lv2', lv2_info.name, true);
      updateParamsFromQueryString('q_str', null, true);

      if (!this.selected_viewer_disclosures) {
        this.fetch_viewer_sb_disclosures({
          report_code: this.current_report_code,
          selected_lv_info: {
            lv1_info: lv1_info,
            lv2_info: lv2_info
          }
        });
      }

      // 선택 후 메뉴 닫기: showDropdown은 true로 유지하여 햄버거 재토글이 즉시 동작하도록 한다.
      // (예전엔 showDropdown=false로 두어 v-if가 풀리면서 모바일에서 두 번째 햄버거 클릭이 안 먹던 버그가 있었음)
      this.isHoverMainMenu = false;
    },
    handleHoverMenuItem(item) {
      this.hoveredMenuItem = item;
      this.$nextTick(() => {
        this.removeBorderFromOtherItems(item);
        this.updateTitleOpacity(item);
      });
    },
    handleHoverMenuItem2(item) {
      this.hoveredMenuItem = item;
      this.$nextTick(() => {
        this.updateTitleOpacity2(item);
      });
    },
    removeBorderFromOtherItems(currentItem) {
      const menuItems = document.querySelectorAll('.nav-item-custom');
      menuItems.forEach(menuItem => {
        if (menuItem.textContent.trim() !== currentItem) {
          menuItem.classList.remove('nav-active-custom');
        }
      });
    },
    resetHoverMenuItem() {
      this.hoveredMenuItem = null;
      this.resetTitleOpacity();
    },
    resetHoverMenuItem2() {
      this.hoveredMenuItem = null;
      this.resetTitleOpacity2();
    },
    updateTitleOpacity(activeItem) {
      this.$nextTick(() => {
        const titles = this.$el.querySelectorAll('.nav-item-custom');
        titles.forEach(title => {
          if (title.textContent.trim() === activeItem) {
            title.style.opacity = '1';
          } else {
            title.style.opacity = '0.5';
          }
        });
      });
    },
    updateTitleOpacity2(activeItem) {
      this.$nextTick(() => {
        const titles = this.$el.querySelectorAll('.submenu-link');
        titles.forEach(title => {
          if (title.textContent.trim() === activeItem) {
            title.style.opacity = '1';
          } else {
            title.style.opacity = '0.5';
          }
        });
      });
    },
    resetTitleOpacity() {
      this.$nextTick(() => {
        const titles = this.$el.querySelectorAll('.nav-item-custom');
        titles.forEach(title => {
          title.style.opacity = '1';
        });
      });
    },
    resetTitleOpacity2() {
      this.$nextTick(() => {
        const titles = this.$el.querySelectorAll('.submenu-link');
        titles.forEach(title => {
          title.style.opacity = '1';
        });
      });
    },
    handleHoverIcon(icon) {
      this.hoveredIcon = icon;
    },
    resetHoverIcon() {
      this.hoveredIcon = null;
    },
    handleScroll() {
      this.scrollY = window.scrollY;
      this.isScrolled = window.scrollY > 50;
    },
    toggleSearchBar() {
      this.showSearchBar = !this.showSearchBar;

      if (this.showSearchBar) {
        // 검색바를 열 때는 햄버거 메뉴를 닫아 UI 충돌을 막는다
        if (this.isHoverMainMenu) {
          this.isHoverMainMenu = false;
        }
        // 양쪽 검색 입력 모두 DOM에는 존재하므로(viewport별 display 차이) 화면 폭으로 선택.
        // MobileSearchBar는 자체 focus() 메서드를 노출, 데스크톱 inline은 b-form-input ref 사용.
        this.$nextTick(() => {
          const isMobileWidth =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(max-width: 767.98px)').matches;
          if (isMobileWidth) {
            const mobileBar = this.$refs.mobileSearchBar;
            if (mobileBar && typeof mobileBar.focus === 'function') {
              mobileBar.focus();
            }
          } else {
            const target = this.$refs.report_search_form;
            if (target && typeof target.focus === 'function') {
              target.focus();
            }
          }
        });
      } else {
        // 검색 바를 닫을 때 검색어 초기화
        this.localSearchKeyword = '';
      }
    },
    updateNavPointColor() {
      const lv1Count = this.sb_toc.length;
      const extra = Math.max(0, lv1Count - 5);
      const multiplier = 1 - 0.06 * extra;
      const offset = this.lang === 'ENG' ? 2 : 0;

      const fontSize1200 = Math.round(14 * multiplier) - offset;
      const fontSize1440 = Math.round(16 * multiplier) - offset;
      const fontSize1680 = Math.round(17 * multiplier) - offset;
      const fontSize1920 = Math.round(18 * multiplier) - offset;

      const submenuFontSize1200 = Math.round(13 * multiplier) - offset;
      const submenuFontSize1440 = Math.round(14 * multiplier) - offset;
      const submenuFontSize1680 = Math.round(15 * multiplier) - offset;
      const submenuFontSize1920 = Math.round(16 * multiplier) - offset;

      const existingStyle = document.getElementById('nav-point-color-style');
      if (existingStyle) existingStyle.remove();

      const styleElement = document.createElement('style');
      styleElement.id = 'nav-point-color-style';
      styleElement.innerHTML = `
          .nav-link-custom {
            padding-left: 0px;
            padding-right: 0px;
            position: relative;
            color: #111111;
            letter-spacing: -.017px;
            line-height: 1.15;
            text-align: center;
            padding-bottom: 3px;
            display: inline-block;
            transition: color 0.3s ease;
            font-weight: 600;

            @media screen and (min-width: 1200px) and (max-width: 1439.98px) {
              font-size: ${fontSize1200}px;
            }

            @media screen and (min-width: 1440px) and (max-width: 1679.98px) {
              font-size: ${fontSize1440}px;
            }

            @media screen and (min-width: 1680px) and (max-width: 1919.98px) {
              font-size: ${fontSize1680}px;
            }

            @media screen and (min-width: 1920px) {
              font-size: ${fontSize1920}px;
            }

            &::after {
              content: "";
              display: block;
              margin: 0 auto;
              width: 0;
              height: 3px;
              background-color: ${this.mainColor};
              transition: width 0.3s ease;
            }

            &:hover {
              color: ${this.mainColor} !important;
            }

            &:hover::after {
              width: 100%;
            }
          }
          .nav-link-custom-active {
            color: ${this.mainColor};
            &::after {
              content: "";
              display: block;
              margin: 0 auto;
              width: 100%;
              height: 3px;
              background-color: ${this.mainColor};
            }
          }
          .disabled-link-active{
            color: ${this.selected_report.sub_color};
          }
          .submenu-link {
            font-weight: 600;
            text-decoration: none;

            @media screen and (min-width: 1200px) and (max-width: 1439.98px) {
              font-size: ${submenuFontSize1200}px;
            }

            @media screen and (min-width: 1440px) and (max-width: 1679.98px) {
              font-size: ${submenuFontSize1440}px;
            }

            @media screen and (min-width: 1680px) and (max-width: 1919.98px) {
              font-size: ${submenuFontSize1680}px;
            }

            @media screen and (min-width: 1920px) {
              font-size: ${submenuFontSize1920}px;
            }

            padding-top: 0.3rem;
            padding-bottom: 0.3rem;
            color: #111111 !important;
          }
        `;
      document.head.appendChild(styleElement);
    },
    handleBackToTranslate() {
      // 번역 페이지(EStoryBoard)로 복귀. 현재 lv1/lv2 query 유지.
      const query = {};
      if (this.$route.query?.lv1) query.lv1 = this.$route.query.lv1;
      if (this.$route.query?.lv2) query.lv2 = this.$route.query.lv2;
      this.$router.push({ name: 'EStoryBoard', query }).catch(() => {
        this.$router.back();
      });
    },
    handleClosePreview() {
      // 커스텀 CSS 에디터에서는 디자인 설정으로 이동
      if (this.$route.path === '/custom_css_editor') {
        this.$router.push({ name: 'ThemeSettings' });
        return;
      }

      // 미리보기 종료 시 현재 선택 정보를 먼저 저장
      const saved_lv1 = this.selected_lv1;
      const saved_lv2 = this.selected_lv2;

      this.setSearchKeyword('');
      this.update_selected_lv1({ lv1_info: null, view_mode: this.is_viewer ? 'viewer' : 'editor' });
      this.update_selected_lv2({ lv2_info: null, view_mode: this.is_viewer ? 'viewer' : 'editor' });

      // URL 쿼리 파라미터 제거
      updateParamsFromQueryString('lv1', null, true);
      updateParamsFromQueryString('lv2', null, true);
      updateParamsFromQueryString('q_str', null, true);

      if (this.globalString) {
        if (this.globalString === 'WriteStoryBoard' && saved_lv1 && saved_lv2) {
          // 만약 globalString 값이 'WriteStoryBoard'이면 해당 페이지로 이동
          this.$router.push({
            name: 'WriteStoryBoard',
            query: {
              lv1: saved_lv1.key,
              lv2: saved_lv2.key
            }
          });
        } else {
          this.$router.push({ name: this.globalString });
        }
      } else {
        // 기본적으로 CheckReport 페이지로 이동
        this.$router.push({ name: 'CheckReport' });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.navbar-sb-wrapper {
  position: sticky;
  top: 0;
  z-index: 1050;
  background-color: white;
}

.navbar-sb {
  z-index: 1050;
  height: 90px;
  left: 50%;
  transform: translateX(-50%);
  transition: all 0.3s ease-in-out;
}

.navbar-sb-menu {
  display: flex;
  flex: 1 0 85%;
  max-width: 85%;
  transition: all 0.3s ease-in-out;

  &-expanded {
    flex: 1 0 100%;
    max-width: 100%;
  }
}

.nav-item-custom {
  flex: 1 1 0;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.nav-item-custom2 {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.nav-link-equal {
  text-align: center;
  width: 100%;
}

.submenu {
  display: flex;
  flex: 1 0 100%;
  position: fixed;
  top: -100%;
  transition: top 0.5s ease-in-out;
  background-color: #fff;
  width: 100%;
  padding-left: 16px;
  padding-right: 16px;
  z-index: 1040;

  &-slide-down {
    top: 90px !important;
  }

  &-title {
    font-weight: 500;
    text-decoration: none;
    font-stretch: normal;
    letter-spacing: -0.017px;

    &:hover,
    &.submenu-active-custom {
      color: #0382df;
    }
  }

  &-link {
    font-weight: 500;
    text-decoration: none !important;

    &:hover,
    &.submenu-active,
    &.nuxt-link-active,
    &.nuxt-link-exact-active {
      color: #111111;
    }
  }

  .custom-col {
    position: relative;
    flex: 1;
  }
}

.navbar-submenu {
  border-left: 1px solid white;
  border-right: 1px solid white;
  transition: all 0.3s ease-in-out;

  &:hover {
    border-left: 1px solid lightgray;
    border-right: 1px solid lightgray;
  }
}

.link-container {
  padding-bottom: 8px;
  padding-top: 8px;
  line-height: 1.1;
}

.submenu-link {
  font-weight: 600;
  text-decoration: none;

  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: #111111 !important;

  &.submenu-active,
  &.nuxt-link-active,
  &.nuxt-link-exact-active {
    color: #111111;
  }

  &:hover {
    color: #111111 !important;
    opacity: 1 !important;
  }

  &.non-hovered {
    opacity: 0.5;
  }
}

.navbar-sb .text-secondary {
  color: rgba(17, 17, 17, 0.5);
  transition: color 0.3s ease, opacity 0.3s ease;

  &.hovered {
    color: #111111;
    opacity: 1;
  }

  &:hover {
    color: #111111;
    opacity: 1;
  }
}

@media screen and (max-width: 1920px) {
  .submenu {
    left: 0;
  }
}

@media screen and (min-width: 1921px) {
  .submenu {
    left: 50%;
    transform: translateX(-50%);
    max-width: 9000px;
  }
}

.disabled-link {
  pointer-events: none;
}

.navbar-sb {
  transition: all 0.3s ease-in-out;
}

.scrolled-navbar {
  border-bottom: 1px solid lightgray;
  position: fixed;
  transition: all 0.3s ease-in-out;
}

.search-input-group {
  border: 1px solid gray;
  border-radius: 25rem;
  overflow: hidden;
}

.search-input {
  border: none;
  border-radius: 0;
  padding: 10px 20px;
  font-size: 20px;
}

.search-input:focus {
  box-shadow: none;
}

.search-button {
  border: none;
  border-radius: 0;
  background-color: transparent;
  padding: 10px 20px;
}

.search-button b-icon {
  color: #fff;
  font-size: 20px;
}

.search-button:hover {
  background-color: #00000011;
}

#web-report-search-result {
  max-height: 600px;
}

.nav-link-custom,
.submenu-link {
  white-space: pre-line;
}

.mobile-submenu {
  .mobile-submenu-lv1 {
    font-size: 1.05rem;
    font-weight: 600;
    padding-top: 0.85rem;
    padding-bottom: 0.85rem;
    text-align: left;
  }

  .mobile-submenu-lv2 {
    font-size: 0.95rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    text-align: left;
    white-space: normal;
    line-height: 1.35;
  }
}

// 모바일/태블릿(xl 미만)에서 우측 아이콘 nav 버튼 사이 간격을 확보.
// Bootstrap navbar의 .nav-link는 기본 가로 padding이 0이라 justify-content-around 만으로는
// 좁은 화면에서 아이콘이 너무 붙어 보임.
@media (max-width: 1199.98px) {
  .navbar-sb .nav-icons-wrap .nav-link {
    padding-left: 0.45rem;
    padding-right: 0.45rem;
  }
}

@media (max-width: 575.98px) {
  // xs에서는 조금 더 넉넉히
  .navbar-sb .nav-icons-wrap .nav-link {
    padding-left: 0.55rem;
    padding-right: 0.55rem;
  }
}

// .mobile-search-bar 스타일은 MobileSearchBar.vue scoped CSS로 이전
</style>

<style lang="scss">
// 터치 전용 디바이스(모바일)에서는 nav 아이콘 버튼 위에 떠 있는 툴팁이
// 첫 탭을 흡수해 버튼 클릭을 막는 문제가 있어 비활성화한다.
// BootstrapVue tooltip은 body에 렌더링되므로 글로벌 셀렉터 사용.
@media (hover: none) and (pointer: coarse) {
  body > .tooltip {
    display: none !important;
  }
}
</style>
